import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  IconButton,
  Modal,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../contexts/MemberContext';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import MemberHeader from '../components/MemberHeader';
import WelcomeSection from '../components/WelcomeSection';
import AlertBanner from '../components/AlertBanner';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'react-qr-code';
import deviceService from '../services/deviceService';

/**
 * Account Verification Pending Page
 * Shows when a member needs to verify their email or phone
 */
const AccountVerificationPendingPage = () => {
  const navigate = useNavigate();
  const { member, orientation } = useMember();
  const [qrModalOpen, setQrModalOpen] = useState(false);

  useEffect(() => {
    if (member.phone_verified && member.email_verified) {
      navigate('/member-dashboard');
    }
  }, [member.phone_verified, member.email_verified]);

  // Handle resend activation SMS
  const handleResendActivation = async () => {
    try {
      await deviceService.resendActivation(member.phone);
      console.log('Activation SMS resent successfully');
    } catch (error) {
      console.error('Error resending activation SMS:', error);
    }
  };

  // Handle QR code modal
  const handleOpenQrModal = () => {
    setQrModalOpen(true);
  };

  const handleCloseQrModal = () => {
    setQrModalOpen(false);
  };

  useEffect(() => {
    if (orientation === -1) {
      // the device was flipped over so navigate to the cashier login page.
      navigate('/cashier-login');
    }
  }, [orientation]);

  return (
    <Container sx={{
      py: 4,
      height: '100%',
      width: '100% !important',
      maxWidth: '1024px !important',
      px: '0 !important',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {/* Navigation header */}
      <MemberHeader />

      {/* Welcome section */}
      <WelcomeSection />

      {/* Verification pending section */}
      <AlertBanner
        title="Account Verification Pending"
        message="To access exclusive coupons and redeem your points, please verify your account."
      >
        <Button
          variant="contained"
          onClick={handleResendActivation}
          sx={{
            bgcolor: 'white',
            color: '#4b5563',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)'
            },
            fontWeight: 'bold',
            px: 2,
            py: 1.5
          }}
        >
          Resend Activation SMS
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            or
          </Typography>

          <Box
            onClick={handleOpenQrModal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              px: 2,
              py: 1,
              borderRadius: 2,
              border: '2px solid rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
                transform: 'scale(1.02)'
              }
            }}
          >
            <QrCode2Icon sx={{ color: 'white' }} />
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
              Show QR code to verify on your phone
            </Typography>
          </Box>
        </Box>
      </AlertBanner>

      {/* Actions section */}
      <Box mt={4} mb={4} display="flex" justifyContent="center" gap={2}>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={() => navigate('/member-confirmation')}
        >
          Continue to earn rewards
        </Button>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>

      {/* QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}
        >
          <IconButton
            onClick={handleCloseQrModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h5" component="h2" gutterBottom>
            Scan to Verify Your Account
          </Typography>

          <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 2 }}>
            <QRCode
              value={`${process.env.REACT_APP_LOYALTY_PORTAL_URL || 'https://members.cinch.io'}/register?phone=${member.phone}`}
              size={300}
              level="H"
              fgColor="#000000"
              bgColor="#ffffff"
            />
          </Box>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
            Scan this QR code with your phone to access the verification portal
          </Typography>
        </Paper>
      </Modal>
    </Container>
  );
};

export default AccountVerificationPendingPage;